import React, { Suspense, useEffect, useState } from "react";
import { toast } from "react-toastify";
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { LoadingButton } from "@mui/lab";
import { Box, Button, CircularProgress, Modal, Typography } from '@mui/material'
import { GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import AuditApi from "api/faber/AuditAPI";
import PaymentAPI from "api/faber/PaymentAPI";
import { AxiosError, AxiosResponse } from "axios";
import MUIDataGrid from "common/AbstractDataGrid/DataGridMUI";
import ColorBox from "common/Box/ColorWhiteBox";
import PageLayoutV2 from "common/Layout/Page/PageLayoutV2";
import LayoutSkeleton from "common/Skeletons/LayoutSkeleton";
import NormalText from "common/Typography/NormalText";
import { format } from "date-fns";
import { FaberResponse } from "helpers/backendTypes";
import OrbitSession from "hooks/useCookies";

const columns: GridColDef[] = [
  // Executed At
  {
    field: 'executedAt',
    headerName: 'Executed At',
    width: 150,
  },
  // Email
  {
    field: 'email',
    headerName: 'User Email',
    width: 150
  },
  // FileName
  {
    field: 'filename',
    headerName: 'File Name',
    width: 150
  },
]

interface UploadHistoryRowDataType {
  executedAt: string
  email: string
  filename: string
}

function ExternalPlatformDashboard() {
  const faberToken = OrbitSession.get('faberToken')
  const pageSize = 10

  const [page, setPage] = useState(1)

  // Modal
  const [showUploadPaidPurchaseCSVModal, setShowUploadPaidPurchaseCSVModal] =
    useState<boolean>(false)
  // Errors
  const [showErrorBox, setShowErrorBox] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  // Success Message
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false)
  const [successMessage, setSuccessMessage] = useState<string>('')

  const [uploadStatus, setUploadStatus] = useState<string>('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [uploadedObject, setUploadedObject] = useState<any>(null)

  const [uploadHistoryData, setUploadHistoryData] = useState<
    UploadHistoryRowDataType[]
  >([])
  const { data: historyData } = useQuery(
    ['historyData', page, pageSize],
    () => AuditApi.getUploadPaidPurchaseAuditLog(faberToken, page, pageSize),
    {
      keepPreviousData: true
    }
  )

  const { mutate: uploadVoucherFile, isLoading: isUploadVoucherFileLoading } =
    useMutation(PaymentAPI.uploadPurchasePaidCsv, {
      onSuccess: (e: AxiosResponse<FaberResponse>) => {
        toast.success('Successfully uploaded CSV')

        const message = e.data.message
        setShowSuccessMessage(true)
        setSuccessMessage(message)
      },
      onError: (err: AxiosError<FaberResponse>) => {
        toast.error(
          'There is a problem trying to upload the voucher file to the server'
        )

        const message = err.response?.data.message
        setShowErrorBox(true)

        // Set it in state to show latter on
        if (message != undefined) {
          // Show the exact error
          setErrorMessage(message)
        }
      }
    })

  const onFileUpload = async (file) => {
    const reader = new FileReader()
    reader.onload = async () => {
      // Set the uploaded file
      setUploadedObject(file)
      setUploadStatus('PENDING')
    }
    reader.readAsText(file)
  }

  useEffect(() => {
    if (uploadStatus === 'PENDING') {
      // Create a form and append the file to the data
      // when the reader finishes reading
      const formData = new FormData()
      formData.append('file', uploadedObject)

      // Upload this form
      uploadVoucherFile({
        faberToken,
        uploadObject: formData
      })
    }
  }, [uploadStatus, uploadedObject])

  useEffect(() => {
    if (historyData?.data) {
      const historyDashboardData = historyData.data.map((row) => {
        return {
          executedAt: format(new Date(row.executedAt), 'dd MMM yyyy HH:mm:ss'),
          email: row.extraFields.Email,
          filename: row.extraFields.FileName
        }
      })
      setUploadHistoryData(historyDashboardData)
    }
  }, [historyData])

  return (
    <Suspense fallback={<LayoutSkeleton />}>
      <Modal
        open={showUploadPaidPurchaseCSVModal}
        onClose={() => setShowUploadPaidPurchaseCSVModal(false)}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={modalStyle}>
          <Typography variant='h4' gutterBottom>
            WARNING: This will likely send an email to EVERY user in the csv.
            Please proceed with caution
          </Typography>

          <Typography variant='h6' gutterBottom>
            This will likely take quite a long time to process (a generous 5
            seconds per row), please manage your time expectations accordingly
          </Typography>

          <Typography variant='h6' color='red'>
            For now, please try to keep the csv rows under 15. Please split the
            csv if it is bigger.
          </Typography>

          {/* Upload CSV button */}
          <LoadingButton
            variant='outlined'
            component='label'
            sx={{ fontSize: '12px', p: 3, background: 'white' }}
            startIcon={<FileUploadIcon />}
            loadingIndicator={<CircularProgress />}
          >
            Upload CSV
            <input
              type='file'
              hidden
              accept='.csv'
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (!e.target.files) return
                // Upload file
                onFileUpload(e.target.files[0])
                // Close the modal
                setShowUploadPaidPurchaseCSVModal(false)
              }}
            />
          </LoadingButton>
        </Box>
      </Modal>

      <PageLayoutV2 header='Manage Voucher'>
        <ColorBox>
          {/* Button to open up a modal */}
          <Button
            variant='outlined'
            sx={{ fontSize: '12px', p: 3, background: 'white' }}
            startIcon={<FileUploadIcon />}
            onClick={() => setShowUploadPaidPurchaseCSVModal(true)}
          >
            Upload file for paid users from external platform
          </Button>
        </ColorBox>
      </PageLayoutV2>

      <PageLayoutV2 header='Response'>
        <ColorBox>
          {/* If it is still processing, show circular progress bar */}
          {isUploadVoucherFileLoading ? <CircularProgress /> : <></>}

          {showSuccessMessage ? (
            <NormalText sx={{ color: 'green' }}>{successMessage}</NormalText>
          ) : (
            <></>
          )}

          {/* If it errored while processing, show the error message */}
          {showErrorBox ? (
            <NormalText sx={{ color: 'red' }}>{errorMessage}</NormalText>
          ) : (
            <></>
          )}
        </ColorBox>
      </PageLayoutV2>

      <PageLayoutV2 header='Upload History'>
        <ColorBox>
          <MUIDataGrid
            columns={columns}
            rows={uploadHistoryData}
            getRowId={(row: UploadHistoryRowDataType) =>
              `${row.executedAt}_${row.email}_${row.filename}`
            }
            // Default sort is by executed at first
            initialState={{
              sorting: {
                sortModel: [{ field: 'executedAt', sort: 'asc' }]
              }
            }}
            pageCount={historyData?.pagination?.totalPages || 0}
            pageNumber={historyData?.pagination?.page || 1}
            onPageChange={(newPage) => {
              setPage(newPage)
            }}
          />
        </ColorBox>
      </PageLayoutV2>
    </Suspense>
  )
}

const modalStyle = {
  position: 'absolute' as const,
  top: '20%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  p: 4
}

export default ExternalPlatformDashboard